import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import {
  designColors,
  designFontFamily,
  designWeights,
} from "services/constants/designTokens";

// Shared "REQUEST DETAILS" card chrome + colored icon-box field row used by
// every My Zone view panel (Leave, Comp Off / Work Adjustment, Attendance
// Correction, Shift Change, Overtime, Advance Request) so they all render the
// same field layout instead of each hand-rolling its own Paper/Card block.
export const detailSectionLabelSx = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: designColors.slate500,
  fontFamily: designFontFamily,
  mb: 1,
};

export const detailCardSx = {
  p: 2,
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
};

// Common accent palette so every panel's field icons draw from the same set
// of colors instead of inventing new ones per screen.
export const detailAccentPalette = {
  indigo: { color: "#5B5FEF", bg: "#EEF2FF" },
  pink: { color: "#DB2777", bg: "#FDF2F8" },
  blue: { color: "#2563EB", bg: "#EFF6FF" },
  green: { color: "#0F9F6E", bg: "#EAFBF2" },
  orange: { color: "#F97316", bg: "#FFF7ED" },
  purple: { color: "#6D5BD0", bg: "#F5F0FF" },
};

export const RequestDetailField = ({
  icon: Icon,
  label,
  value,
  helper,
  accent,
  rightPill,
  isLast,
  multiline = false,
}) => (
  <Box>
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: accent.bg,
          color: accent.color,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            color: designColors.slate600,
            fontFamily: designFontFamily,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.25,
          }}
        >
          {label}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 0.45, minWidth: 0 }}
        >
          <Typography
            title={typeof value === "string" ? value : undefined}
            sx={{
              color: designColors.slate900,
              fontFamily: designFontFamily,
              fontSize: multiline ? 13.5 : 15.5,
              fontWeight: designWeights.semibold,
              lineHeight: 1.35,
              minWidth: 0,
              overflow: multiline ? "visible" : "hidden",
              textOverflow: multiline ? "clip" : "ellipsis",
              whiteSpace: multiline ? "normal" : "nowrap",
            }}
          >
            {value || "-"}
          </Typography>
          {rightPill ? (
            <Box
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 1,
                backgroundColor: accent.bg,
                color: accent.color,
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: designFontFamily,
              }}
            >
              {rightPill}
            </Box>
          ) : null}
        </Stack>
        {helper ? (
          <Typography
            sx={{
              mt: 0.25,
              color: designColors.slate600,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: designFontFamily,
            }}
          >
            {helper}
          </Typography>
        ) : null}
      </Box>
    </Stack>
    {isLast ? null : <Divider sx={{ my: 1.6, borderColor: designColors.slate100 }} />}
  </Box>
);

export default RequestDetailField;
